import { useMemo, useState } from 'react';
import { getNSplitOptionsForRange, type NSplitOption } from '../../apps/cidr-planner/splitPlanner';
import { isAwsSubnetAllowed, usableIpCount } from '../../apps/cidr-planner/usableIpUtils';
import type { AddressRange, IpCountMode } from '../../apps/cidr-planner/types';
import type { DisplayLang } from '../../../shared/utils/pageDisplay';

const TEXT = {
  ja: {
    splitCount: '分割数',
    splitOption: (n: number, prefix: number) => `${n}分割 (/${prefix} × ${n})`,
    name: '名前（任意・連番付与）',
    nameHint: '入力すると各ブロックに「名前 1」「名前 2」…を付けます',
    usable: '各ブロックの使用可能 IP',
    preview: '作成される CIDR',
    noOption: 'これ以上分割できません',
    awsNoSplit: 'AWS VPC では /28 より細かく分割できません',
    split: '分割する',
    twoWayHint: '2分割なら CIDR 境界に沿って余りなく分割できます（例: /30 → /31 × 2）',
  },
  en: {
    splitCount: 'Split count',
    splitOption: (n: number, prefix: number) => `${n}-way (/${prefix} × ${n})`,
    name: 'Name (optional, numbered)',
    nameHint: 'If set, blocks become "Name 1", "Name 2", …',
    usable: 'Usable IPs per block',
    preview: 'CIDRs to create',
    noOption: 'Cannot split further',
    awsNoSplit: 'AWS VPC cannot split smaller than /28',
    split: 'Split',
    twoWayHint: 'A 2-way split fills the range with no leftover (e.g. /30 → /31 × 2)',
  },
} as const;

type NSplitFieldsProps = {
  range: AddressRange;
  ipCountMode: IpCountMode;
  lang: DisplayLang;
  initialName?: string;
  onSplit: (payloads: Array<{ cidr: string; name: string }>) => void;
};

export function NSplitFields({
  range,
  ipCountMode,
  lang,
  initialName = '',
  onSplit,
}: NSplitFieldsProps) {
  const t = TEXT[lang];
  const allOptions = useMemo(() => getNSplitOptionsForRange(range), [range]);
  const options = useMemo(
    () => allOptions.filter((option) => isAwsSubnetAllowed(option.prefix, ipCountMode)),
    [allOptions, ipCountMode],
  );
  const preferredN = options.find((option) => option.n === 2)?.n ?? options[0]?.n ?? 2;
  const [splitN, setSplitN] = useState<number>(preferredN);
  const [name, setName] = useState(initialName);

  const selected = options.find((option) => option.n === splitN) ?? options[0];

  if (options.length === 0) {
    return (
      <p className="cidr-sheet__hint">
        {ipCountMode === 'aws' && allOptions.length > 0 ? t.awsNoSplit : t.noOption}
      </p>
    );
  }

  return (
    <>
      <label className="cidr-sheet__field">
        <span className="cidr-sheet__label">{t.splitCount}</span>
        <select
          className="cidr-sheet__select"
          value={selected?.n ?? splitN}
          onChange={(event) => setSplitN(Number(event.target.value))}
        >
          {options.map((option) => (
            <option key={option.n} value={option.n}>
              {t.splitOption(option.n, option.prefix)}
            </option>
          ))}
        </select>
      </label>
      {selected && (
        <>
          {ipCountMode === 'standard' && selected.n === 2 && (
            <p className="cidr-sheet__hint">{t.twoWayHint}</p>
          )}
          <p className="cidr-sheet__hint">
            {t.usable}: {usableIpCount(selected.prefix, ipCountMode)}
          </p>
          <div className="cidr-sheet__field">
            <span className="cidr-sheet__label">{t.preview}</span>
            <ul className="cidr-sheet__preview-list">
              {selected.cidrs.map((cidr) => (
                <li key={cidr}>{cidr}</li>
              ))}
            </ul>
          </div>
        </>
      )}
      <label className="cidr-sheet__field">
        <span className="cidr-sheet__label">{t.name}</span>
        <input
          className="cidr-sheet__input"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <p className="cidr-sheet__hint">{t.nameHint}</p>
      </label>
      <button
        type="button"
        className="cidr-sheet__btn cidr-sheet__btn--primary cidr-sheet__btn--full"
        disabled={!selected}
        onClick={() => {
          if (!selected) {
            return;
          }
          const trimmedName = name.trim();
          onSplit(
            selected.cidrs.map((cidr, index) => ({
              cidr,
              name: trimmedName ? `${trimmedName} ${index + 1}` : '',
            })),
          );
        }}
      >
        {t.split}
      </button>
    </>
  );
}

export type { NSplitOption };
